/**
 * Per-frame content metrics for the fight-pose art (`assets/omerta/fight/<class>/<pose>.webp`).
 *
 * QA (86cat3wgq): "fighters render at different sizes, feet don't sit on the
 * same line." Root cause is in the ART, not the layout — every pose is an
 * independently generated cutout, not frames of one shared template, so each
 * one bakes in its OWN transparent margin (headroom above the head, a gap
 * below the feet) and its OWN canvas aspect ratio. FightScreen used to render
 * every pose at a flat `height: 100%` of the fighter box; that scales the
 * baked-in padding right along with the character, so two fighters standing
 * in the identical box read as different heights, feet at different levels —
 * exactly the QA report (measured: warrior's default frame carries ~7.9%
 * bottom padding, paladin's only ~4.2% — at the same box height that's a
 * visible few-percent difference in where the feet land).
 *
 * `c` — the character's own silhouette height, as a fraction of the full
 *       image height (from the top of the alpha bounding box to the bottom).
 * `b` — the transparent gap below the feet, as a fraction of the full image
 *       height (i.e. how much of the canvas is empty floor/shadow-less air
 *       under the character).
 *
 * Measured once against the shipped webp set (alpha-channel bounding box per
 * file) and baked in here — this is presentation metadata about static art,
 * not gameplay data, so it lives beside the UI that consumes it rather than
 * re-scanning a canvas on every pose swap during a fight.
 */
export const FIGHT_POSE_METRICS: Record<string, Record<string, { c: number; b: number }>> = {
  ARCHER: {
    'ability': { c: 0.8816, b: 0.0695 },
    'damage-2': { c: 0.8647, b: 0.0902 },
    'damage': { c: 0.9135, b: 0.047 },
    'default': { c: 0.9173, b: 0.0526 },
    'defeat': { c: 0.8177, b: 0.0977 },
    'flee': { c: 0.9267, b: 0.0489 },
    'guard': { c: 0.9305, b: 0.0602 },
    'heavy': { c: 0.809, b: 0.105 },
    'patch-up': { c: 0.8365, b: 0.1034 },
    'strike': { c: 0.8816, b: 0.0808 },
    'win': { c: 0.8628, b: 0.0714 },
  },
  BRUISER: {
    'damage': { c: 0.859, b: 0.079 },
    'default': { c: 0.851, b: 0.071 },
    'defeat': { c: 0.705, b: 0.111 },
    'guard': { c: 0.837, b: 0.077 },
    'heavy': { c: 0.845, b: 0.083 },
    'strike': { c: 0.815, b: 0.115 },
    'win': { c: 0.851, b: 0.071 },
  },
  GOON: {
    'damage': { c: 0.826, b: 0.086 },
    'default': { c: 0.834, b: 0.081 },
    'defeat': { c: 0.709, b: 0.114 },
    'guard': { c: 0.835, b: 0.08 },
    'heavy': { c: 0.843, b: 0.085 },
    'strike': { c: 0.791, b: 0.125 },
    'win': { c: 0.836, b: 0.08 },
  },
  HITMAN: {
    'damage': { c: 0.843, b: 0.082 },
    'default': { c: 0.826, b: 0.085 },
    'defeat': { c: 0.76, b: 0.056 },
    'guard': { c: 0.824, b: 0.083 },
    'heavy': { c: 0.852, b: 0.087 },
    'strike': { c: 0.814, b: 0.095 },
    'win': { c: 0.827, b: 0.085 },
  },
  KINGPIN: {
    'damage': { c: 0.829, b: 0.08 },
    'default': { c: 0.841, b: 0.081 },
    'defeat': { c: 0.707, b: 0.113 },
    'guard': { c: 0.834, b: 0.082 },
    'heavy': { c: 0.875, b: 0.083 },
    'strike': { c: 0.795, b: 0.111 },
    'win': { c: 0.842, b: 0.08 },
  },
  LEGBREAKER: {
    'damage': { c: 0.862, b: 0.082 },
    'default': { c: 0.846, b: 0.077 },
    'defeat': { c: 0.744, b: 0.074 },
    'guard': { c: 0.848, b: 0.101 },
    'heavy': { c: 0.909, b: 0.08 },
    'strike': { c: 0.811, b: 0.113 },
    'win': { c: 0.846, b: 0.077 },
  },
  MAGE: {
    'ability': { c: 0.8283, b: 0.0922 },
    'damage': { c: 0.8883, b: 0.0552 },
    'default': { c: 0.8703, b: 0.0733 },
    'defeat': { c: 0.7285, b: 0.113 },
    'flee': { c: 0.9091, b: 0.0537 },
    'guard': { c: 0.8856, b: 0.0616 },
    'heavy': { c: 0.9104, b: 0.0387 },
    'patch-up': { c: 0.9231, b: 0.0479 },
    'strike': { c: 0.8517, b: 0.0948 },
    'win': { c: 0.8668, b: 0.072 },
  },
  PALADIN: {
    'ability': { c: 0.8712, b: 0.0697 },
    'damage': { c: 0.9077, b: 0.0488 },
    'default': { c: 0.918, b: 0.0418 },
    'defeat': { c: 0.9228, b: 0.0463 },
    'flee': { c: 0.8591, b: 0.0732 },
    'guard': { c: 0.8787, b: 0.0635 },
    'heavy': { c: 0.8507, b: 0.0995 },
    'patch-up': { c: 0.9461, b: 0.0312 },
    'strike': { c: 0.8466, b: 0.0841 },
    'win': { c: 0.9738, b: 0.0102 },
  },
  THUG: {
    'damage': { c: 0.851, b: 0.079 },
    'default': { c: 0.843, b: 0.077 },
    'defeat': { c: 0.691, b: 0.117 },
    'guard': { c: 0.834, b: 0.077 },
    'heavy': { c: 0.868, b: 0.086 },
    'strike': { c: 0.797, b: 0.122 },
    'win': { c: 0.844, b: 0.076 },
  },
  WARRIOR: {
    'ability': { c: 0.8269, b: 0.101 },
    'damage-2': { c: 0.8638, b: 0.1042 },
    'damage': { c: 0.8558, b: 0.0817 },
    'default': { c: 0.885, b: 0.0785 },
    'defeat': { c: 0.7019, b: 0.1154 },
    'flee': { c: 0.8301, b: 0.0913 },
    'guard': { c: 0.8061, b: 0.1154 },
    'heavy': { c: 0.859, b: 0.071 },
    'patch-up': { c: 0.891, b: 0.0897 },
    'strike': { c: 0.8177, b: 0.1184 },
    'win': { c: 0.891, b: 0.0689 },
  },
};

/** Content-fraction floor. Without this, an outlier frame (a kneeling
 *  "defeat" pose can run as low as ~0.70 content) would need to render ~40%
 *  taller than the fighter box to fully normalize — a jarring pop rather
 *  than a subtle correction. Clamping trades perfect alignment on the
 *  rarest, briefest frames (end-of-bout only) for a bounded, always-sane
 *  render size everywhere else. */
const MIN_CONTENT = 0.75;

/**
 * CSS values that make a pose's CHARACTER (not its padded canvas) fill the
 * fighter box and stand with its feet on the box's own bottom edge.
 *
 * `height` replaces the flat `100%` so the box scales to the character's own
 * content ratio, not the full padded canvas. `translateY` (a percentage of
 * the element's OWN rendered height — scale-invariant, so it needs no extra
 * math against `height`) then slides the image down by exactly its baked-in
 * foot-to-canvas-edge gap, so the feet — not the transparent margin below
 * them — land on the box's bottom edge. The box is bottom-anchored via
 * `align-items: flex-end` (see .fs-fighter), so sliding down by the residual
 * bottom padding is what actually plants the feet on the ground line.
 *
 * Falls back to identity (today's un-corrected behaviour) for anything not
 * in the table above — a new pose without measurements never breaks, it
 * just isn't corrected yet.
 */
export function poseFit(cls?: string | null, pose?: string | null): { height: string; translateY: number } {
  const m = cls && pose ? FIGHT_POSE_METRICS[cls.toUpperCase()]?.[pose] : undefined;
  if (!m) return { height: '100%', translateY: 0 };
  const c = Math.max(m.c, MIN_CONTENT);
  return { height: `calc(100% / ${c})`, translateY: Math.round(m.b * 10000) / 100 };
}
